import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  QaiFlexContainerComponent,
  QaiCallInsightsCardComponent,
  QaiProgressBarComponent,
} from '@qai/ui';
import { ActivatedRoute } from '@angular/router';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { AppConstants } from 'src/app/app-constants';
import { IHighlightCard } from 'src/app/interfaces/reportingV2/ihighlight-card';
import {
  HIGHLIGHT_CARD_TYPES,
} from 'src/app/utils/constants/reporting-dashboard/highlight-card.constants';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoModule } from '@jsverse/transloco';
import { HighlightCardMappingUtil } from 'src/app/utils/highlight-card-mapping.util';
import { IInsightCard } from 'src/app/interfaces/reportingV2/iinsight-card';

@Component({
  selector: 'app-user-highlight-cards',
  templateUrl: './user-highlight-cards.component.html',
  styleUrls: ['./user-highlight-cards.component.scss'],
  standalone: true,
  imports: [
    QaiFlexContainerComponent,
    QaiCallInsightsCardComponent,
    QaiProgressBarComponent,
    TranslocoModule,
  ],
})
export class UserHighlightCardsComponent implements OnInit {
  insightCards: IInsightCard[] = [];
  isLoading = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private reportingDashboardService: ReportingDashboardService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
      const userUuid = this.route.snapshot.paramMap.get('id');

      if (!accountUuid || !userUuid) {
        console.warn('Missing accountUuid or userUuid for highlight cards');
        return;
      }

      this.isLoading = true;
      this.reportingDashboardService
        .getHighlightCards({ accountUuid, userUuid, teamUuids: [] }, withDemoModule(AppConstants.NEW_REPORTING_UI))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (cards) => {
            this.insightCards = this.mapToInsightCards(cards);
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Error loading highlight cards:', error);
            this.isLoading = false;
          },
        });
    });
  }

  private mapToInsightCards(cards: IHighlightCard[]): IInsightCard[] {
    return cards.map((card, index) => {
      const isMethodology =
        card.cardType === HIGHLIGHT_CARD_TYPES.HIGHEST_SCORING_METHODOLOGY ||
        card.cardType === HIGHLIGHT_CARD_TYPES.LOWEST_SCORING_METHODOLOGY;
      const max = isMethodology ? 5 : 100;
      const labelVariant: 'percentage' | 'value-max' = isMethodology ? 'value-max' : 'percentage';
      const segments = isMethodology ? 5 : 1;
      const heading = this.translationService.translate(HighlightCardMappingUtil.getHeadingKey(card.cardType), 'reportingV2');

      console.log('Mapping card:', card, 'to insight card with heading:', heading);
      const isNegative = card.averageValue < 0;
      const absValue = Math.abs(card.averageValue);
      const descriptionKey = HighlightCardMappingUtil.getDescriptionKey(card.cardType, isNegative);
      const progressDescription = descriptionKey ? this.translationService.translate(descriptionKey, 'reportingV2') : '';

      return {
        id: `${card.cardType}_${index}`,
        heading,
        description: card.cardType !== HIGHLIGHT_CARD_TYPES.USER_ENGAGEMENT ? (card.themeTitle ?? '') : '',
        headingMode: 'small',
        descriptionMode: 'simple',
        showLink: false,
        subHeading: card.callName ?? undefined,
        subheadingIcon: card.callName ? 'phone-call' : undefined,
        progressBar: {
          value: absValue,
          max,
          kind: this.getProgressBarKind(card),
          shape: 'round',
          size: 'medium',
          label: String(Math.round(absValue)),
          labelVariant,
          description: progressDescription,
          segments,
        },
      };
    });
  }

  private getProgressBarKind(
    card: IHighlightCard
  ): 'success' | 'danger' | 'warning' | 'info' {
    switch (card.cardType) {
      case HIGHLIGHT_CARD_TYPES.LEAST_MISSED_QUESTION:
      case HIGHLIGHT_CARD_TYPES.HIGHEST_SCORING_METHODOLOGY:
        return 'success';
      case HIGHLIGHT_CARD_TYPES.MOST_MISSED_QUESTION:
      case HIGHLIGHT_CARD_TYPES.LOWEST_SCORING_METHODOLOGY:
      case HIGHLIGHT_CARD_TYPES.LEAST_ARTICULATED_MESSAGE:
        return 'danger';
      case HIGHLIGHT_CARD_TYPES.USER_ENGAGEMENT:
        return card.averageValue >= 0 ? 'success' : 'danger';
      default:
        return 'info';
    }
  }
}
